import Layout from '../components/Layout';
import { useAppDispatch, useAppSelector } from './hooks';
import { setSiteId, setSiteIdMetadata } from '@/features/user/userSlice';
import { createContext, useEffect, useMemo, useState } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, PaletteMode, Box } from '@mui/material';
import Spinner from '../components/Shared/Spinner';
import Navbar from '../components/Navbar/Navbar';
import { Outlet, useParams } from 'react-router';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ErrorSitePage from '../components/Shared/ErrorSitePage';
import { getDesignTokens } from '@/theme';
import { useGetClientQuery } from '@/features/api/client.api';
import { ClientConfig } from '@/types/types';
import { AssetTypeEnum } from '@/types/enums';

declare global {
  interface Window {
    pendo: any;
  }
}

export const ColorModeContext = createContext({
  toggleColorMode: (mode: PaletteMode) => {
    return mode;
  },
});

function App() {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<PaletteMode>('light');
  const { siteMetadata: siteIdMetadata } = useAppSelector((state: any) => {
    return state.user;
  });
  const siteId = useParams()?.siteId?.toLowerCase() || '';

  const {
    data: queryResult,
    isFetching: isFetchingClient,
    isLoading: isLoadingClient,
  } = useGetClientQuery({ siteId });

  const client = queryResult as ClientConfig;
  const { palette } = createTheme();
  const theme = useMemo(
    () => createTheme(getDesignTokens(mode, palette)),
    [mode],
  );

  useEffect(() => {
    if (!client) return;
    if (siteId) {
      dispatch(setSiteId({ siteId }));
    }
    dispatch(
      setSiteIdMetadata({
        ...client,
        assetType: client.assetType || AssetTypeEnum.CAR,
      }),
    );
  }, [client]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: (mode: PaletteMode) => {
        setMode(mode);
        return mode;
      },
    }),
    [],
  );

  if (isFetchingClient && isLoadingClient) {
    return <Spinner />;
  }

  return (
    <HelmetProvider>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div
            style={{ fontFamily: theme.typography.fontFamily }}
            className="App"
          >
            <Helmet>
              <link
                rel="icon"
                type="image/png"
                href={
                  siteIdMetadata?.faviconLocation !== null
                    ? siteIdMetadata.faviconLocation
                    : 'https://storage.googleapis.com/cox2m-dealers-ui-logos/57x57.png'
                }
              />
            </Helmet>
            <header className="App-header">
              {client?.siteId ? (
                <Layout>
                  <Navbar />
                  <Box
                    sx={{
                      paddingTop: '10px',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Outlet />
                  </Box>
                </Layout>
              ) : (
                <ErrorSitePage />
              )}
            </header>
          </div>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </HelmetProvider>
  );
}

export default App;
